<script>
  import { onMount, untrack } from 'svelte';
  import { session } from './lib/state/session.svelte.js';
  import { auth, restoreSession, openLoginModal } from './lib/state/auth.svelte.js';
  import IntentWizard from './lib/components/IntentWizard.svelte';
  import AsyncPhase from './lib/components/AsyncPhase.svelte';
  import SaveAndReloadButton from './lib/components/SaveAndReloadButton.svelte';
  import StudioSettingsPanel from './lib/components/StudioSettingsPanel.svelte';
  import LoginButton from './lib/components/LoginButton.svelte';
  import UserBalance from './lib/components/UserBalance.svelte';
  import EmailVerificationBanner from './lib/components/EmailVerificationBanner.svelte';
  import PurchaseModal from './lib/components/PurchaseModal.svelte';
  import SubscriptionModal from './lib/components/SubscriptionModal.svelte';
  import ChargeConfirmationModal from './lib/components/ChargeConfirmationModal.svelte';
  import CreditRecoveryModal from './lib/components/CreditRecoveryModal.svelte';
  import { creditRecovery } from './lib/state/credit-recovery.svelte.js';
  import SessionStoragePanel from './lib/components/SessionStoragePanel.svelte';
  import { sessionPersistence, startSessionPersistence, captureCreativeSession,
    queueSessionSave, flushSessionSave } from './lib/state/session-persistence.svelte.js';
  import { billing, initBilling } from './lib/state/billing.svelte.js';
  import { checkHealth } from './lib/utils/api.js';
  import { consumeProtocolHandoffNavigation } from './lib/utils/link-navigation.js';
  import { ttsSettings, ttsPlayback, toggleTTS, getVariantCount } from './lib/state/settings.svelte.js';
  import { loadFavorites, favoriteCount, requestPersistentStorage } from './lib/state/gallery.svelte.js';

  let healthCheckComplete = $state(false);
  let serverError = $state(null);

  onMount(startSessionPersistence);

  $effect(() => {
    if (!sessionPersistence.ready || !sessionPersistence.accountId) return;
    const snapshot = captureCreativeSession();
    untrack(() => queueSessionSave(snapshot));
  });

  $effect(() => {
    if (!sessionPersistence.ready) return;
    const path = session.phase === 'finalized' ? '/gallery' : session.phase === 'studio' ? '/studio' : '/';
    history.replaceState({ phase: session.phase }, '', path);
  });

  // Phase information for header
  const phases = [
    { id: 'wizard', name: 'Intent Wizard', description: 'Describe your vision' },
    { id: 'visualizer', name: 'Visualizer', description: 'Facet your prompt' },
    { id: 'confirmation', name: 'Confirm', description: 'Review combinations' },
    { id: 'studio', name: 'Studio', description: 'Generate & refine' }
  ];

  // 'finalized' (gallery) isn't a step of its own — treat it as everything
  // complete so the progress bar reads full instead of collapsing to zero.
  const currentPhaseIndex = $derived(
    session.phase === 'finalized' ? phases.length : phases.findIndex(p => p.id === session.phase)
  );

  const renderPromptCount = $derived(
    session.branches.find(branch => branch.id === session.currentBranchId)?.iterations.length
      ? getVariantCount()
      : session.creativeBrief?.facetedPrompts?.length || getVariantCount()
  );

  let bannerDismissed = $state(false);
  const showLoginBanner = $derived(!auth.isLoggedIn && !auth.isRestoring && !bannerDismissed);

  const homepageExamplePool = [
    {
      src: '/examples/aleyna.webp',
      alt: 'Fantasy portrait with glowing neon accents',
      title: 'Neon Character Portrait',
      tags: ['Character', 'Glowing Neon']
    },
    {
      src: '/examples/complete-6495853f-c8d1-4a2b-b807-a1e41a961c6b.webp',
      alt: 'Vibrant surreal scene with cinematic lighting',
      title: 'Cinematic Surreal Scene',
      tags: ['Cinematic', 'Surreal']
    },
    {
      src: '/examples/complete-ad07e028-ed6b-48bb-adcb-3762b027bf8d.webp',
      alt: 'Dreamlike character concept rendered in high detail',
      title: 'Dream Concept Art',
      tags: ['Concept Art', 'High Detail']
    },
    {
      src: '/examples/photo-2026-02-22-13-55-13.webp',
      alt: 'Atmospheric art piece with bold color contrast',
      title: 'Atmospheric Contrast',
      tags: ['Atmospheric', 'Bold Color']
    },
    {
      src: '/examples/photo-2026-02-22-13-55-23.webp',
      alt: 'Illustrative scene with painterly texture and glow',
      title: 'Painterly Glow',
      tags: ['Painterly', 'Illustrative']
    },
    {
      src: '/examples/photo-2026-02-22-13-55-29.webp',
      alt: 'Moody concept image with strong focal lighting',
      title: 'Moody Light Study',
      tags: ['Moody', 'Focal Light']
    },
    {
      src: '/examples/photo-2026-02-22-13-56-28.webp',
      alt: 'Soft surreal composition with vivid accents',
      title: 'Soft Surreal Mood',
      tags: ['Soft Mood', 'Vivid']
    },
    {
      src: '/examples/slothicorn.webp',
      alt: 'Playful fantasy creature concept art',
      title: 'Playful Creature',
      tags: ['Creature', 'Whimsical']
    }
  ];
  const HOMEPAGE_EXAMPLE_COUNT = 4;
  const HOMEPAGE_AUTO_ROTATE_MS = 10000;
  let homepageExamples = $state([]);
  let isRefreshingExamples = $state(false);
  let refreshRequestId = 0;
  let showcaseInterval = null;

  function shuffleList(items) {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  function getExamplesSignature(items) {
    return items
      .map(item => item.src)
      .sort()
      .join('|');
  }

  function preloadHomepageExamples(examples) {
    const preloadTasks = examples.map((example) => new Promise((resolve) => {
      const img = new Image();
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      // Guard against hanging loads so rotation cannot stall indefinitely.
      const timeoutId = setTimeout(finish, 8000);
      const finishAndClear = () => {
        clearTimeout(timeoutId);
        finish();
      };
      img.src = example.src;
      if (img.complete && img.naturalWidth > 0) {
        finishAndClear();
        return;
      }
      img.onload = finishAndClear;
      img.onerror = finishAndClear;
    }));
    return Promise.all(preloadTasks);
  }

  async function refreshHomepageExamples() {
    if (isRefreshingExamples) return;
    isRefreshingExamples = true;
    const activeRequestId = ++refreshRequestId;

    const currentSignature = getExamplesSignature(homepageExamples);
    let nextExamples = shuffleList(homepageExamplePool).slice(0, HOMEPAGE_EXAMPLE_COUNT);
    let nextSignature = getExamplesSignature(nextExamples);
    let attempts = 0;

    while (attempts < 5 && nextSignature === currentSignature) {
      nextExamples = shuffleList(homepageExamplePool).slice(0, HOMEPAGE_EXAMPLE_COUNT);
      nextSignature = getExamplesSignature(nextExamples);
      attempts += 1;
    }

    try {
      await preloadHomepageExamples(nextExamples);
      if (activeRequestId !== refreshRequestId) return;
      homepageExamples = nextExamples;
    } finally {
      if (activeRequestId === refreshRequestId) {
        isRefreshingExamples = false;
      }
    }
  }

  // Check server health on mount and restore session if user was previously logged in
  onMount(async () => {
    try {
      try {
        await checkHealth();
      } catch (firstError) {
        // One quiet retry — a transient blip shouldn't show the error screen
        await new Promise(resolve => setTimeout(resolve, 2000));
        await checkHealth();
      }
      healthCheckComplete = true;

      // Ask the browser to protect IndexedDB from eviction (Safari clears
      // unprotected storage after 7 days of inactivity), then load favorites.
      requestPersistentStorage().catch(() => {});
      await loadFavorites();

      // Probe the shared Sogni cookie; creative sessions hydrate after account verification.
      try {
        await restoreSession();
      } catch (restoreError) {
        console.error('Session restore failed:', restoreError);
      }

      // Wire billing focus/broadcast refresh + handle checkout redirect-backs.
      initBilling();
    } catch (error) {
      serverError = 'We could not reach the Sogni Vibe service.';
      console.error('Server health check failed:', error);
    }
  });

  // Re-check auth session when user returns to the tab (e.g. after login redirect)
  onMount(() => {
    let lastAuthProbe = 0;
    const refreshAuthSession = async () => {
      if (auth.isLoggedIn || auth.isRestoring) return;
      // Throttle: a guest alt-tabbing shouldn't re-probe on every focus.
      const now = Date.now();
      if (now - lastAuthProbe < 30000) return;
      lastAuthProbe = now;
      try {
        // Silent so the LOGIN button / guest banner don't flicker mid-probe.
        await restoreSession({ silent: true });
      } catch (restoreError) {
        console.error('Session refresh failed:', restoreError);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAuthSession();
      }
    };

    window.addEventListener('focus', refreshAuthSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshAuthSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  onMount(() => {
    refreshHomepageExamples();

    showcaseInterval = setInterval(() => {
      if (document.hidden || session.phase !== 'wizard') {
        return;
      }
      refreshHomepageExamples();
    }, HOMEPAGE_AUTO_ROTATE_MS);

    return () => {
      if (showcaseInterval) {
        clearInterval(showcaseInterval);
        showcaseInterval = null;
      }
    };
  });

  // Warn only when this tab still holds work that has not been saved.
  onMount(() => {
    const handleBeforeUnload = (e) => {
      // A mailto/tel handoff is not leaving Vibe, even though Chrome may emit
      // beforeunload while it launches the registered external app.
      if (consumeProtocolHandoffNavigation()) return;

      if (session.phase !== 'wizard' && session.branches.length > 0 &&
        (!sessionPersistence.savedAt || sessionPersistence.isSaving || sessionPersistence.error)) {
        e.preventDefault();
        e.returnValue = 'Some work has not finished saving on this device.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    const saveOnHide = () => { if (document.hidden) void flushSessionSave(); };
    window.addEventListener('pagehide', flushSessionSave);
    document.addEventListener('visibilitychange', saveOnHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', flushSessionSave);
      document.removeEventListener('visibilitychange', saveOnHide);
    };
  });

  // URL Routing for /gallery
  function navigateToGallery() {
    history.pushState({ phase: 'finalized' }, '', '/gallery');
    session.phase = 'finalized';
  }

  function navigateToStudio() {
    history.pushState({ phase: 'studio' }, '', '/studio');
    session.phase = 'studio';
  }

  function handleSignIn() {
    // In-app auth (SSO-aware). The modal offers both sign-in and sign-up.
    openLoginModal();
  }

  // Handle URL routing on mount and popstate
  onMount(() => {
    // Handle initial URL on page load
    const path = window.location.pathname;
    if (path === '/gallery') {
      session.phase = 'finalized';
    } else if (path === '/studio') {
      // Only navigate to studio if we have a creative brief, otherwise redirect to wizard
      if (session.creativeBrief) {
        session.phase = 'studio';
      } else {
        history.replaceState(null, '', '/');
        session.phase = 'wizard';
      }
    }

    // Listen for browser back/forward navigation
    const handlePopState = (event) => {
      if (event.state?.phase) {
        session.phase = event.state.phase;
      } else {
        // Fallback based on URL
        const path = window.location.pathname;
        if (path === '/gallery') session.phase = 'finalized';
        else if (path === '/studio') session.phase = 'studio';
        else session.phase = 'wizard';
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  });
</script>

<main class="app-container" class:has-banner={healthCheckComplete && !serverError && showLoginBanner}>
  {#if serverError}
    <div class="server-error">
      <div class="error-content">
        <h2>Connection Trouble</h2>
        <p>{serverError}</p>
        <p class="error-instructions">
          This is usually temporary — check your internet connection and try again in a moment.
        </p>
        <button
          class="btn-primary"
          onclick={() => location.reload()}
        >
          Try Again
        </button>
      </div>
    </div>
  {:else if !healthCheckComplete}
    <div class="loading-screen">
      <div class="spinner large"></div>
      <h2>Connecting to Sogni Vibe...</h2>
    </div>
  {:else}
    <!-- Phase Indicator Header -->
    <div class="phase-indicator">
      <div class="phase-container">
        <div class="settings-row">
          <!-- Always-visible Gallery button -->
          <button
            class="gallery-btn"
            onclick={navigateToGallery}
            title="View Gallery"
            aria-label="Gallery"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
            </svg>
            Gallery
            {#if favoriteCount.value > 0}
              <span class="gallery-badge">{favoriteCount.value}</span>
            {/if}
          </button>
          {#if auth.isLoggedIn}
            <UserBalance />
          {/if}
          <button
            class="tts-global-toggle"
            class:active={ttsSettings.enabled}
            class:speaking={ttsPlayback.isSpeaking}
            onclick={toggleTTS}
            title={ttsPlayback.isSpeaking ? 'Speaking - click to stop' : ttsSettings.enabled ? 'Voice enabled - click to disable' : 'Voice disabled - click to enable'}
            aria-label={ttsPlayback.isSpeaking ? 'Stop speaking' : ttsSettings.enabled ? 'Disable voice' : 'Enable voice'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              {#if ttsSettings.enabled}
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
              {:else}
                <line x1="23" y1="9" x2="17" y2="15"/>
                <line x1="17" y1="9" x2="23" y2="15"/>
              {/if}
            </svg>
          </button>
          <LoginButton />
          <SessionStoragePanel />
          <StudioSettingsPanel promptCount={renderPromptCount} />
        </div>
        <div class="phase-steps">
          <!-- Background connector line -->
          <div class="phase-line-bg"></div>
          <!-- Filled progress line -->
          <div class="phase-line-fill" style="transform: scaleX({currentPhaseIndex > 0 ? Math.min(currentPhaseIndex / (phases.length - 1), 1) : 0})"></div>

          {#each phases as phase, index}
            <div class="phase-item" class:active={index === currentPhaseIndex} class:completed={index < currentPhaseIndex}>
              <div class="phase-number">
                {#if index < currentPhaseIndex}
                  ✓
                {:else}
                  {index + 1}
                {/if}
              </div>
              <div class="phase-content">
                <div class="phase-name">{phase.name}</div>
                <div class="phase-description">{phase.description}</div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>

    <EmailVerificationBanner />

    <svelte:boundary onerror={(error) => console.error('Unexpected UI error:', error)}>
    {#if !sessionPersistence.ready}
      <div class="loading-screen" role="status">
        <div class="spinner large"></div>
        <h2>Restoring your creative session...</h2>
      </div>
    {:else if session.phase === 'wizard'}
      {#if !session.hasSubmittedInitialPrompt && homepageExamples.length > 0}
        <section class="homepage-showcase" aria-label="Example creations from Sogni Vibe">
          <div class="showcase-grid">
            {#each homepageExamples as example, index (example.src)}
              <article class="showcase-card" style={`--stagger: ${index * 70}ms`}>
                <img
                  src={example.src}
                  alt={example.alt}
                  width="384"
                  height="384"
                  loading="eager"
                  decoding="async"
                />
                <div class="showcase-meta">
                  <h3>{example.title}</h3>
                  <div class="showcase-tags">
                    {#each example.tags as tag}
                      <span>{tag}</span>
                    {/each}
                  </div>
                </div>
              </article>
            {/each}
          </div>
        </section>
      {/if}
      {#if !session.hasSubmittedInitialPrompt}
        <p class="creation-overview">
          Explore the live image catalog, edit original source images, and choose Draft or Final.
          Use saved originals for upscaling, background removal, and object cutouts in Gallery.
          Animate Gallery favorites with WAN, LTX-2.5, MiniMax H3 or paid partner video models.
          Check what Unlimited and Unlimited Pro cover in Settings, and review charges before paid generation.
          Edit saved favorites from their originals, or export images with prompt, model and seed metadata from Gallery.
        </p>
      {/if}
      <IntentWizard />
    {:else if ['visualizer', 'confirmation', 'studio', 'finalized'].includes(session.phase)}
      <AsyncPhase phase={session.phase} />
    {/if}

    {#snippet failed(error, reset)}
      <div class="boundary-error">
        <h2>Something went wrong</h2>
        <p>An unexpected error interrupted the app. Your saved favorites are safe.</p>
        <div class="boundary-error-actions">
          <button class="btn-primary" onclick={reset}>Try Again</button>
          <SaveAndReloadButton />
        </div>
      </div>
    {/snippet}
    </svelte:boundary>

    <!-- Not logged in banner - shown across phases until dismissed -->
    {#if showLoginBanner}
      <div class="login-warning">
        <span class="login-warning-text">
          You are not logged in. <button class="login-warning-btn" onclick={handleSignIn}>Sign in free</button> to create — prompts and images run on your own Sogni account.
        </span>
        <button class="login-warning-dismiss" onclick={() => bannerDismissed = true} aria-label="Dismiss">✕</button>
      </div>
    {/if}

    {#if billing.showPurchaseModal}
      <PurchaseModal />
    {/if}
    {#if billing.showSubscriptionModal}
      <SubscriptionModal />
    {/if}
    <ChargeConfirmationModal />
    {#if creditRecovery.show && !billing.showPurchaseModal && !billing.showSubscriptionModal}
      <CreditRecoveryModal />
    {/if}
  {/if}
</main>

<style>
  .app-container {
    min-height: 100vh;
    min-height: 100dvh; /* Dynamic viewport height for mobile */
    display: flex;
    flex-direction: column;
    /* iPhone safe area support */
    padding-top: env(safe-area-inset-top, 0);
  }

  .loading-screen {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
  }

  .loading-screen h2 {
    color: var(--color-primary);
  }

  .server-error {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%);
  }

  .error-content {
    background: white;
    padding: 3rem;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    max-width: 600px;
    text-align: center;
  }

  .error-content h2 {
    color: var(--color-error);
    margin-bottom: 1rem;
  }

  .error-content p {
    color: var(--color-text-muted);
    margin-bottom: 1rem;
  }

  .error-instructions {
    background: var(--color-surface);
    padding: 1rem;
    border-radius: var(--radius);
    margin: 2rem 0;
  }


  /* Phase Indicator Styles */
  .phase-indicator {
    background: #ffffff;
    border-bottom: 1px solid var(--color-border);
    padding: 1.5rem 0;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .phase-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .settings-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 639px) {
    .settings-row {
      margin-bottom: 0.375rem;
    }
  }

  .phase-steps {
    display: flex;
    position: relative;
  }

  /* Background line connecting all phases */
  .phase-line-bg,
  .phase-line-fill {
    position: absolute;
    top: 16px;
    /* 12.5% = center of first item (half of 25%), 87.5% = center of last item */
    left: 12.5%;
    right: 12.5%;
    height: 2px;
  }

  .phase-line-bg {
    background: var(--color-border);
    z-index: 0;
  }

  /* Filled portion of the line showing progress */
  .phase-line-fill {
    background: var(--color-primary);
    z-index: 1;
    transform-origin: left center;
    transition: transform 0.4s ease;
  }

  .phase-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    z-index: 2;
    transition: all 0.3s ease;
    flex: 1;
    min-width: 0;
  }

  .phase-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.875rem;
    background: #ffffff;
    border: 2px solid var(--color-border);
    color: var(--color-text-muted);
    transition: all 0.3s ease;
    flex-shrink: 0;
    position: relative;
    z-index: 3;
  }

  .phase-item.active .phase-number {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
    box-shadow: 0 0 0 4px rgba(255, 27, 141, 0.15);
  }

  .phase-item.completed .phase-number {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }

  .phase-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    text-align: center;
  }

  .phase-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-text-muted);
    transition: color 0.3s ease;
    white-space: nowrap;
  }

  .phase-item.active .phase-name {
    color: var(--color-text);
  }

  .phase-item.completed .phase-name {
    color: var(--color-primary);
  }

  .phase-description {
    font-size: 0.7rem;
    color: var(--color-text-muted);
    opacity: 0.7;
  }

  .phase-item.active .phase-description {
    color: var(--color-primary);
    opacity: 1;
  }

  /* Mobile responsive - compact header */
  @media (max-width: 639px) {
    .phase-indicator {
      padding: 0.5rem 0;
      border-bottom-width: 1px;
    }

    .phase-container {
      padding: 0 0.75rem;
    }

    .phase-line-bg,
    .phase-line-fill {
      top: 12px;
    }

    .phase-number {
      width: 24px;
      height: 24px;
      font-size: 0.7rem;
    }

    .phase-name {
      font-size: 0.55rem;
    }

    .phase-description {
      display: none;
    }

    .phase-item {
      gap: 0.25rem;
    }

    .phase-content {
      gap: 0;
    }
  }

  /* Tablet responsive */
  @media (min-width: 640px) and (max-width: 768px) {
    .phase-description {
      display: none;
    }
  }

  /* Global TTS Toggle Button */
  .tts-global-toggle {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    border-radius: 50%;
    background: var(--color-surface);
    border: 2px solid var(--color-border);
    color: var(--color-text-muted);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tts-global-toggle:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-light);
  }

  .tts-global-toggle.active {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .tts-global-toggle.speaking {
    animation: pulse-speaker 1s ease-in-out infinite;
  }

  @keyframes pulse-speaker {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 27, 141, 0.4);
    }
    50% {
      transform: scale(1.1);
      box-shadow: 0 0 0 8px rgba(255, 27, 141, 0);
    }
  }

  .tts-global-toggle svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 639px) {
    .tts-global-toggle {
      width: 32px;
      height: 32px;
      min-width: 32px;
      min-height: 32px;
    }

    .tts-global-toggle svg {
      width: 16px;
      height: 16px;
    }
  }

  /* Gallery Button - Always Visible */
  .gallery-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 36px;
    padding: 0 12px;
    border-radius: 18px;
    background: none;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    font-size: 0.875rem;
    transition: color 0.2s ease;
  }

  .gallery-btn:hover {
    color: var(--color-primary);
  }

  .gallery-btn svg {
    width: 16px;
    height: 16px;
  }

  .gallery-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--color-primary);
    color: white;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1;
  }

  .homepage-showcase {
    width: min(980px, 100%);
    margin: 0.9rem auto 0;
    padding: 0.8rem 0.9rem 0.5rem;
    border: 1px solid var(--color-border);
    border: 1px solid color-mix(in srgb, var(--color-border) 82%, #ffffff);
    border-radius: 16px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 253, 0.94) 100%);
  }

  .creation-overview {
    width: min(800px, 100%);
    margin: 0.75rem auto 0;
    padding: 0 1rem;
    color: var(--color-text-muted);
    font-size: 0.85rem;
    line-height: 1.5;
    text-align: center;
  }

  .showcase-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.65rem;
    margin-bottom: 0.2rem;
  }

  .showcase-card {
    position: relative;
    overflow: hidden;
    border-radius: 11px;
    border: 1px solid var(--color-border);
    aspect-ratio: 1 / 0.9;
    box-shadow: 0 12px 25px -20px rgba(0, 0, 0, 0.45);
    opacity: 0;
    transform: translateY(8px);
    animation: showcase-enter 0.4s ease forwards;
    animation-delay: var(--stagger, 0ms);
  }

  .showcase-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.35s ease;
  }

  .showcase-card:hover img {
    transform: scale(1.04);
  }

  .showcase-meta {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0.55rem;
    color: #ffffff;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(5, 8, 18, 0.84) 100%);
    pointer-events: none;
  }

  .showcase-meta h3 {
    margin: 0;
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1.2;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  }

  .showcase-tags {
    margin-top: 0.3rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .showcase-tags span {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 0.45rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.12);
    font-size: 0.65rem;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1;
    backdrop-filter: blur(2px);
  }

  @keyframes showcase-enter {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 639px) {
    .gallery-btn {
      height: 32px;
      padding: 0 10px;
      font-size: 0.75rem;
    }

    .gallery-btn svg {
      width: 14px;
      height: 14px;
    }

    .gallery-badge {
      min-width: 16px;
      height: 16px;
      font-size: 0.625rem;
    }

    .homepage-showcase {
      margin-top: 0.6rem;
      padding: 0.65rem 0.65rem 0.45rem;
      border-radius: 14px;
    }

    .showcase-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.35rem;
      margin-bottom: 0.15rem;
    }

    .showcase-card {
      border-radius: 8px;
      aspect-ratio: 1 / 1;
    }

    .showcase-meta {
      display: none;
    }
  }

  @media (min-width: 640px) and (max-width: 1023px) {
    .showcase-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  /* Reserve room for the fixed banner so it never covers page content.
     --banner-height lets fixed children (e.g. gallery action bar) stack
     above the banner instead of colliding with it. */
  .app-container {
    --banner-height: 0px;
  }

  .app-container.has-banner {
    --banner-height: calc(46px + env(safe-area-inset-bottom, 0));
    padding-bottom: calc(56px + env(safe-area-inset-bottom, 0));
  }

  /* Banner copy wraps to two lines on small screens */
  @media (max-width: 639px) {
    .app-container.has-banner {
      --banner-height: calc(66px + env(safe-area-inset-bottom, 0));
      padding-bottom: calc(76px + env(safe-area-inset-bottom, 0));
    }
  }

  /* Login Warning Banner */
  .login-warning {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #fef3cd;
    border-top: 1px solid #ffc107;
    color: #856404;
    text-align: center;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    font-size: 0.875rem;
    z-index: 1000;
  }

  .login-warning-dismiss {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #856404;
    opacity: 0.7;
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0.5rem;
    line-height: 1;
  }

  .login-warning-dismiss:hover {
    opacity: 1;
  }

  .boundary-error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 3rem 1.5rem;
    gap: 0.75rem;
  }

  .boundary-error h2 {
    color: var(--color-text);
  }

  .boundary-error p {
    color: var(--color-text-muted);
    max-width: 420px;
  }

  .boundary-error-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .login-warning-btn {
    background: none;
    border: none;
    color: #856404;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }

  .login-warning-btn:hover {
    color: #533f03;
  }

  @media (max-width: 639px) {
    .login-warning {
      font-size: 0.75rem;
      padding: 0.5rem 0.75rem;
      padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0));
    }
  }
</style>
